
import Vue from 'vue';
import EntityMetaDialog from '@/components/misc/EntityMetaDialog.vue';

import TagService from '@/services/TagService';
import DeviceService from '@/services/DeviceService';
import PropertyService from '@/services/PropertyService';
import ConnectorService from '@/services/ConnectorService';

export default Vue.extend({
  name: 'ViewInfoDialog',

  components: { EntityMetaDialog },

  props: {
    view: { type: Object, required: true },
  },

  data: () => ({
    localdialog: false,
    loading: false,

    alertText: '',
    alertVisible: false,
    alertType: 'error',

    prettyConfig: {} as any,

    tags: [] as any[],
    devices: [] as any[],
    properties: [] as any[],
    connectors: [] as any[],
  }),

  methods: {
    clear() {
      this.alertVisible = false;
      this.alertText = '';
      this.alertType = 'error';
      this.loading = false;
      this.prettyConfig = {};
    },

    dismiss() {
      this.clear();
      this.localdialog = false;
    },

    tAttr(attributeKey: string): string {
      return String(
        ((this as any).$t?.(`attribute.${attributeKey}`) as any) || attributeKey
      );
    },

    tEntity(entityKey: string): string {
      return String(
        ((this as any).$t?.(`entity.${entityKey}`) as any) || entityKey
      );
    },

    labelForKey(configKey: string): string {
      const translationKey = configKey.includes('.')
        ? configKey
        : `attribute.${configKey}`;
      const translated = (this as any).$t?.(translationKey) as any;
      return String(translated || configKey);
    },

    groupTitle(groupIndex: number): string {
      const translationKey = 'view.filterGroup';
      const translated = (this as any).$t?.(translationKey, {
        index: groupIndex + 1,
      }) as any;

      if (translated && translated !== translationKey)
        return String(translated);
      return `Group ${groupIndex + 1}`;
    },

    entityTitle(entityKey: string): string {
      const translationKey = `entity.${entityKey}`;
      const translated = (this as any).$t?.(translationKey) as any;
      if (translated && translated !== translationKey)
        return String(translated);

      return entityKey.charAt(0).toUpperCase() + entityKey.slice(1);
    },

    prettyFieldLabel(rawFieldName: string): string {
      const translationKey = `attribute.${rawFieldName}`;
      const translated = (this as any).$t?.(translationKey) as any;
      if (translated && translated !== translationKey)
        return String(translated);

      return String(rawFieldName)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (firstChar) => firstChar.toUpperCase());
    },

    isEmptyValue(valueToCheck: any): boolean {
      if (valueToCheck === null || valueToCheck === undefined) return true;
      if (typeof valueToCheck === 'string') return valueToCheck.trim() === '';
      if (Array.isArray(valueToCheck)) return valueToCheck.length === 0;
      if (typeof valueToCheck === 'object')
        return Object.keys(valueToCheck).length === 0;
      return false;
    },

    resolveTag(tagId: string): string {
      const foundTag = this.tags.find((tag: any) => tag.uuid === tagId);
      return foundTag && foundTag.name ? foundTag.name : tagId;
    },

    resolveDevice(deviceId: string): string {
      const foundDevice = this.devices.find(
        (device: any) => device.uuid === deviceId
      );
      if (foundDevice)
        return foundDevice.aksId || foundDevice.description || deviceId;
      return deviceId;
    },

    resolveProperty(propertyId: string): string {
      const foundProperty = this.properties.find(
        (property: any) => property.uuid === propertyId
      );
      return foundProperty && foundProperty.name
        ? foundProperty.name
        : propertyId;
    },

    resolveConnector(connectorId: string): string {
      const foundConnector = this.connectors.find(
        (connector: any) => connector.uuid === connectorId
      );
      return foundConnector && foundConnector.name
        ? foundConnector.name
        : connectorId;
    },

    formatValueForDisplay(attributeKey: string, rawValue: any): string {
      if (this.isEmptyValue(rawValue)) return '';

      const normalizedAttributeKey = String(attributeKey || '').toLowerCase();

      if (normalizedAttributeKey === 'tagids') {
        const tagArray = Array.isArray(rawValue) ? rawValue : [rawValue];
        const tagNames = tagArray
          .map((tagItem: any) => tagItem != null ? this.resolveTag(String(tagItem)) : '')
          .filter(Boolean);
        return tagNames.join(', ');
      }

      if (normalizedAttributeKey === 'deviceid')
        return typeof rawValue === 'string' ? this.resolveDevice(rawValue) : String(rawValue);
      if (normalizedAttributeKey === 'propertyid')
        return typeof rawValue === 'string' ? this.resolveProperty(rawValue) : String(rawValue);
      if (normalizedAttributeKey === 'connectorid')
        return typeof rawValue === 'string' ? this.resolveConnector(rawValue) : String(rawValue);

      if (Array.isArray(rawValue)) {
        const items = rawValue
          .map((arrayItem) => {
            if (arrayItem === null || arrayItem === undefined) return '';
            return String(arrayItem);
          })
          .filter(Boolean);

        return items.join(', ');
      }

      return String(rawValue);
    },

    addSectionIfExists(sections: any[], entityKey: string, filterObject: any) {
      if (!filterObject || this.isEmptyValue(filterObject)) return;

      const displayLines = this.linesForEntityFilter(entityKey, filterObject);
      if (!displayLines || displayLines.length === 0) return;

      sections.push({
        title: this.entityTitle(entityKey),
        lines: displayLines,
      });
    },

    linesForEntityFilter(entityKey: string, filterObject: any): string[] {
      const lines: string[] = [];

      const fromLabel = this.tAttr('from');
      const untilLabel = this.tAttr('until');
      const flagLabel = this.tAttr('flag');
      const tagsLabel = this.tAttr('tags');

      if (entityKey === 'reading') {
        if (filterObject.from)
          lines.push(`${fromLabel} = ${String(filterObject.from)}`);
        if (filterObject.until)
          lines.push(`${untilLabel} = ${String(filterObject.until)}`);
        if (filterObject.flag)
          lines.push(`${flagLabel} = ${String(filterObject.flag)}`);

        if (filterObject.createdFrom)
          lines.push(
            `${this.prettyFieldLabel('createdFrom')} = ${String(
              filterObject.createdFrom
            )}`
          );
        if (filterObject.createdUntil)
          lines.push(
            `${this.prettyFieldLabel('createdUntil')} = ${String(
              filterObject.createdUntil
            )}`
          );

        return lines;
      }

      const singleEntityLabel = this.tEntity(entityKey);

      let pluralEntityKey = 'devices';
      if (entityKey === 'property') pluralEntityKey = 'properties';
      if (entityKey === 'connector') pluralEntityKey = 'connectors';
      const pluralEntityLabel = this.tEntity(pluralEntityKey);

      if (filterObject.id) {
        const resolveEntity = (val: any): string => {
          if (val == null) return '';
          const s = String(val);
          if (entityKey === 'device') return this.resolveDevice(s);
          if (entityKey === 'property') return this.resolveProperty(s);
          if (entityKey === 'connector') return this.resolveConnector(s);
          return s;
        };
        const entityName = resolveEntity(filterObject.id);
        if (entityName) lines.push(`${singleEntityLabel} = ${entityName}`);
      }

      if (Array.isArray(filterObject.ids) && filterObject.ids.length) {
        const resolveEntity = (val: any): string => {
          if (val == null) return '';
          const s = String(val);
          if (entityKey === 'device') return this.resolveDevice(s);
          if (entityKey === 'property') return this.resolveProperty(s);
          if (entityKey === 'connector') return this.resolveConnector(s);
          return s;
        };
        const entityNames = filterObject.ids
          .map((idValue: any) => resolveEntity(idValue))
          .filter(Boolean);

        if (entityNames.length)
          lines.push(`${pluralEntityLabel} = ${entityNames.join(', ')}`);
        else lines.push(`${pluralEntityLabel} = (${filterObject.ids.length})`);
      }

      if (Array.isArray(filterObject.tagIds) && filterObject.tagIds.length) {
        const tagNames = filterObject.tagIds
          .map((tagIdValue: any) => tagIdValue != null ? this.resolveTag(String(tagIdValue)) : '')
          .filter(Boolean);
        if (tagNames.length)
          lines.push(`${tagsLabel} = ${tagNames.join(', ')}`);
      }

      if (filterObject.createdFrom)
        lines.push(
          `${this.prettyFieldLabel('createdFrom')} = ${String(
            filterObject.createdFrom
          )}`
        );
      if (filterObject.createdUntil)
        lines.push(
          `${this.prettyFieldLabel('createdUntil')} = ${String(
            filterObject.createdUntil
          )}`
        );

      return lines;
    },

    findLookupNeeds(filtersArray: any) {
      const lookupNeeds = {
        tags: false,
        devices: false,
        properties: false,
        connectors: false,
      };

      if (!Array.isArray(filtersArray)) return lookupNeeds;

      for (let groupIndex = 0; groupIndex < filtersArray.length; groupIndex++) {
        const filterGroup = filtersArray[groupIndex];

        const deviceFilter = filterGroup && filterGroup.device;
        const propertyFilter = filterGroup && filterGroup.property;
        const connectorFilter = filterGroup && filterGroup.connector;

        if (deviceFilter && deviceFilter.tagIds && deviceFilter.tagIds.length)
          lookupNeeds.tags = true;
        if (
          propertyFilter &&
          propertyFilter.tagIds &&
          propertyFilter.tagIds.length
        )
          lookupNeeds.tags = true;
        if (
          connectorFilter &&
          connectorFilter.tagIds &&
          connectorFilter.tagIds.length
        )
          lookupNeeds.tags = true;

        if (
          deviceFilter &&
          (deviceFilter.id || (deviceFilter.ids && deviceFilter.ids.length))
        )
          lookupNeeds.devices = true;
        if (
          propertyFilter &&
          (propertyFilter.id ||
            (propertyFilter.ids && propertyFilter.ids.length))
        )
          lookupNeeds.properties = true;
        if (
          connectorFilter &&
          (connectorFilter.id ||
            (connectorFilter.ids && connectorFilter.ids.length))
        )
          lookupNeeds.connectors = true;
      }

      return lookupNeeds;
    },

    loadLookupsIfNeeded(requestConfig: any) {
      const tenantId = this.currentTenant;

      const requestConfigKeys = Object.keys(requestConfig || {}).map(
        (keyName) => keyName.toLowerCase()
      );

      const lookupNeedsFromFilters = this.findLookupNeeds(
        requestConfig && requestConfig.filters
      );

      const shouldLoadTags =
        lookupNeedsFromFilters.tags ||
        requestConfigKeys.some(
          (keyName) => keyName.includes('tagids')
        );

      const shouldLoadDevices =
        lookupNeedsFromFilters.devices ||
        requestConfigKeys.some(
          (keyName) => keyName.includes('deviceid')
        );

      const shouldLoadProperties =
        lookupNeedsFromFilters.properties ||
        requestConfigKeys.some(
          (keyName) => keyName.includes('propertyid')
        );

      const shouldLoadConnectors =
        lookupNeedsFromFilters.connectors ||
        requestConfigKeys.some(
          (keyName) => keyName.includes('connectorid')
        );

      const lookupPromises: Promise<any>[] = [];

      if (shouldLoadTags && this.tags.length === 0) {
        lookupPromises.push(
          TagService.getAllCollectionPages(tenantId).then(
            (loadedTags: any[]) => {
              this.tags = loadedTags || [];
            }
          )
        );
      }

      if (shouldLoadDevices && this.devices.length === 0) {
        lookupPromises.push(
          DeviceService.getAllCollectionPages(tenantId).then(
            (loadedDevices: any[]) => {
              this.devices = loadedDevices || [];
            }
          )
        );
      }

      if (shouldLoadProperties && this.properties.length === 0) {
        lookupPromises.push(
          PropertyService.getAllCollectionPages(tenantId).then(
            (loadedProperties: any[]) => {
              this.properties = loadedProperties || [];
            }
          )
        );
      }

      if (shouldLoadConnectors && this.connectors.length === 0) {
        lookupPromises.push(
          ConnectorService.getAllCollectionPages(tenantId).then(
            (loadedConnectors: any[]) => {
              this.connectors = loadedConnectors || [];
            }
          )
        );
      }

      return Promise.all(lookupPromises);
    },

    buildPrettyConfig() {
      this.loading = true;
      this.alertVisible = false;

      const requestConfigRaw =
        ((this.view as any) && (this.view as any).requestConfig) || {};

      this.loadLookupsIfNeeded(requestConfigRaw)
        .then(() => {
          const prettyRequestConfig: any = { ...requestConfigRaw };

          Object.keys(prettyRequestConfig).forEach((prettyKey) => {
            if (prettyKey === 'filters') return;
            if (this.isEmptyValue(prettyRequestConfig[prettyKey]))
              delete prettyRequestConfig[prettyKey];
          });

          this.prettyConfig = prettyRequestConfig;
          this.loading = false;
        })
        .catch((error: any) => {
          this.loading = false;
          this.alertVisible = true;
          this.alertType = 'error';
          this.alertText = String(error && (error.message || error));
        });
    },
  },
  watch: {
    localdialog(isOpen: boolean) {
      if (isOpen) {
        this.buildPrettyConfig();
      } else {
        this.clear();
      }
    },

    view: {
      deep: true,
      handler() {
        if (this.localdialog) this.buildPrettyConfig();
      },
    },
  },
  computed: {
    currentTenant(): string {
      return (this as any).$store.state.session.selectedTenant.uuid;
    },

    prettyEntries(): any[] {
      const prettyConfigObject = this.prettyConfig || {};
      const entries: any[] = [];

      Object.keys(prettyConfigObject).forEach((configKey) => {
        const configValue = prettyConfigObject[configKey];
        const normalizedKey = String(configKey || '').toLowerCase();

        if (normalizedKey === 'filters' || normalizedKey.endsWith('.filters')) {
          entries.push({
            key: configKey,
            label: this.labelForKey(configKey),
            type: 'filters',
          });
          return;
        }

        const formattedValue = this.formatValueForDisplay(
          configKey,
          configValue
        );
        if (!formattedValue) return;

        if (
          normalizedKey === 'from' ||
          normalizedKey === 'until' ||
          normalizedKey === 'flag'
        ) {
          entries.push({
            key: configKey,
            label: this.tAttr(normalizedKey),
            value: formattedValue,
            type: 'default',
          });
          return;
        }

        entries.push({
          key: configKey,
          label: this.labelForKey(configKey),
          value: formattedValue,
          type: 'default',
        });
      });

      return entries;
    },

    filterDisplayGroups(): any[] {
      const filtersArray = this.prettyConfig && this.prettyConfig.filters;
      if (!Array.isArray(filtersArray)) return [];

      const displayGroups: any[] = [];

      for (let groupIndex = 0; groupIndex < filtersArray.length; groupIndex++) {
        const filterGroup = filtersArray[groupIndex];
        const sections: any[] = [];

        this.addSectionIfExists(
          sections,
          'reading',
          filterGroup && filterGroup.reading
        );
        this.addSectionIfExists(
          sections,
          'device',
          filterGroup && filterGroup.device
        );
        this.addSectionIfExists(
          sections,
          'property',
          filterGroup && filterGroup.property
        );
        this.addSectionIfExists(
          sections,
          'connector',
          filterGroup && filterGroup.connector
        );

        const summaryTitles = sections
          .map((section: any) => section.title)
          .slice(0, 3);

        const summary =
          summaryTitles.length > 0
            ? summaryTitles.join(', ') + (sections.length > 3 ? '…' : '')
            : '-';

        displayGroups.push({
          title: this.groupTitle(groupIndex),
          summary,
          sections,
        });
      }

      return displayGroups;
    },
  },
});
